import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { BookOpen, Calculator, ChevronRight, X, CheckCircle, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ReviewModuleSelect() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [moduleStats, setModuleStats] = useState({
    rw_module_1: { total: 0, correct: 0 },
    rw_module_2: { total: 0, correct: 0 },
    math_module_1: { total: 0, correct: 0 },
    math_module_2: { total: 0, correct: 0 }
  });

  useEffect(() => {
    document.title = 'Review Questions | Practice4SAT';
  }, []);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      // Try to get diagnostic session first
      try {
        const diagnosticResponse = await axios.get(`${API}/diagnostic/${sessionId}/score`);
        if (diagnosticResponse.data) {
          const diagnosticSession = await axios.get(`${API}/diagnostic/${sessionId}/review`);
          setResults(diagnosticSession.data);
          calculateModuleStats(diagnosticSession.data.questions);
          return;
        }
      } catch (error) {
        console.debug('Not a diagnostic session, loading regular test');
      }
      
      // Regular test session
      const sessions = await axios.get(`${API}/test-sessions/${sessionId}`);
      const testId = sessions.data.test_id;
      
      const sessionResponse = await axios.get(`${API}/tests/${testId}/results/${sessionId}`);
      setResults(sessionResponse.data);
      calculateModuleStats(sessionResponse.data.questions);
    } catch (error) {
      toast.error('Failed to load review');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const calculateModuleStats = (questions) => {
    const stats = {
      rw_module_1: { total: 0, correct: 0 },
      rw_module_2: { total: 0, correct: 0 },
      math_module_1: { total: 0, correct: 0 },
      math_module_2: { total: 0, correct: 0 }
    };

    questions.forEach(q => {
      const section = q.section || 'math';
      const module = q.module || 'module_1';
      
      // Normalize module name
      let moduleKey;
      if (module === 'module_1') {
        moduleKey = `${section}_module_1`;
      } else {
        // module_2a, module_2b, 2A, 2B all map to module_2
        moduleKey = `${section}_module_2`;
      }

      if (stats[moduleKey]) {
        stats[moduleKey].total += 1;
        if (q.is_correct) {
          stats[moduleKey].correct += 1;
        }
      }
    });

    setModuleStats(stats);
  };

  const handleExit = () => {
    navigate('/dashboard');
  };

  const getModuleColor = (section) => {
    return section === 'rw' ? '#6366f1' : '#0030b3';
  };

  const getModuleIcon = (section) => {
    return section === 'rw' ? BookOpen : Calculator;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading review...</div>
      </div>
    );
  }

  const modules = [
    { key: 'rw_module_1', section: 'rw', name: 'Reading & Writing', module: 'Module 1', moduleNum: 1 },
    { key: 'rw_module_2', section: 'rw', name: 'Reading & Writing', module: 'Module 2', moduleNum: 2 },
    { key: 'math_module_1', section: 'math', name: 'Math', module: 'Module 1', moduleNum: 1 },
    { key: 'math_module_2', section: 'math', name: 'Math', module: 'Module 2', moduleNum: 2 }
  ];

  // Exit button for review header
  const exitButton = (
    <Button 
      onClick={() => setShowExitDialog(true)}
      variant="outline"
      data-testid="exit-review-btn"
      style={{ 
        background: 'white',
        color: '#0030b3',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <X size={20} />
      Exit to Dashboard
    </Button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header 
        variant="minimal" 
        logoClickPath="/dashboard"
        rightContent={exitButton}
      />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '800px', width: '100%' }}>
          <h1 style={{ 
            fontSize: '1.75rem', 
            fontWeight: '700', 
            color: '#1a2332', 
            marginBottom: '0.5rem',
            textAlign: 'center'
          }}>
            {results?.test_title || 'Test'} Review
          </h1>
          <p style={{ 
            fontSize: '1rem', 
            color: '#666', 
            marginBottom: '2.5rem',
            textAlign: 'center'
          }}>
            Select a module to review your answers
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            {modules.map((mod) => {
              const stats = moduleStats[mod.key];
              const IconComponent = getModuleIcon(mod.section);
              const color = getModuleColor(mod.section);
              const incorrect = stats.total - stats.correct;
              
              return (
                <button
                  key={mod.key}
                  onClick={() => navigate(`/review/${sessionId}/${mod.section}/${mod.moduleNum}`)}
                  data-testid={`module-btn-${mod.key}`}
                  style={{
                    background: 'white',
                    border: `2px solid ${color}20`,
                    borderRadius: '16px',
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = color;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 8px 24px ${color}20`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = `${color}20`;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ 
                      background: `${color}15`,
                      borderRadius: '12px',
                      padding: '0.75rem',
                      display: 'inline-flex'
                    }}>
                      <IconComponent size={24} color={color} />
                    </div>
                    <ChevronRight size={20} color="#999" />
                  </div>
                  
                  <div>
                    <h3 style={{ 
                      fontSize: '1.1rem', 
                      fontWeight: '600', 
                      color: '#1a2332',
                      marginBottom: '0.25rem'
                    }}>
                      {mod.name}
                    </h3>
                    <p style={{ 
                      fontSize: '0.9rem', 
                      color: '#666',
                      margin: 0
                    }}>
                      {mod.module}
                    </p>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    gap: '1rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #eee'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <CheckCircle size={16} color="#22c55e" />
                      <span style={{ fontSize: '0.85rem', color: '#22c55e', fontWeight: '600' }}>
                        {stats.correct}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <XCircle size={16} color="#ef4444" />
                      <span style={{ fontSize: '0.85rem', color: '#ef4444', fontWeight: '600' }}>
                        {incorrect}
                      </span>
                    </div>
                    <span style={{ fontSize: '0.85rem', color: '#999' }}>
                      / {stats.total} questions
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Back to Analytics button */}
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Button
              variant="outline"
              onClick={() => navigate(`/analytics/${sessionId}`)}
              data-testid="back-to-analytics-btn"
              style={{
                color: '#666',
                borderColor: '#ddd'
              }}
            >
              Back to Analytics
            </Button>
          </div>
        </div>
      </div>

      <Footer />

      {/* Exit Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return to Dashboard?</DialogTitle>
            <DialogDescription>
              You can always review your test results from the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowExitDialog(false)} style={{ flex: 1 }}>
              Stay Here
            </Button>
            <Button onClick={handleExit} style={{ flex: 1, background: '#0030b3' }}>
              Go to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
